import { Task } from "@lit/task";
import { DASHBOARD_CONFIG } from "@pentacode/core";
import { css, html, LitElement } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { app } from "../../../../init";
import { debounce } from "../../../../lib/util";
import { shared } from "../../../../styles";
import "./base-tile";

@customElement("ptc-dashboard-tile-notes")
export class DashboardTileNotes extends LitElement {
    @property({ attribute: false })
    size: { inline: number; block: number } = app.settings.dashboard.tiles.notes.size;

    @state()
    private _isSaving = false;

    @state()
    private _lastSavedContent?: string;

    private _notesTask = new Task(this, {
        task: async () => {
            if (app.account?.admin && !app.profile?.id) {
                return "admin_without_profile" as const;
            }
            const note = await app.api.getNote("textNote");
            // @TODO: sanitize note content
            if (note) {
                this._lastSavedContent = note.content;
            }
            return note;
        },
        args: () => [],
    });

    private _typedContent?: string;

    get hasPendingSave() {
        return this._isSaving || (this._typedContent !== undefined && this._typedContent !== this._lastSavedContent);
    }

    private _debouncedSave = debounce(async (content: string) => {
        if (content === this._lastSavedContent) {
            return;
        }
        this._isSaving = true;
        try {
            await app.api.saveNote({ content, type: "textNote" });
            this._lastSavedContent = content;
        } catch (error) {
            console.error("Failed to save note:", error);
        } finally {
            this._isSaving = false;
        }
    }, 2000);

    private _unregisterChangeListener: () => void;
    connectedCallback(): void {
        super.connectedCallback();
        // tiptap (~564KB, vendor-editor) loads on demand when a notes tile mounts.
        void import("../../../../components/rich-text/rich-text-editor");
        const listener = () => {
            void this._notesTask.run();
        };
        app.registerRefreshableTarget("notes", listener);
        this._unregisterChangeListener = () => {
            app.removeRefreshableTarget("notes", listener);
        };
    }

    disconnectedCallback() {
        super.disconnectedCallback();

        if (this._unregisterChangeListener) {
            this._unregisterChangeListener();
        }
    }

    private _handleContentChange(event: CustomEvent) {
        this._scheduleSave(event.detail.content);
    }

    private _scheduleSave(content: string) {
        this._typedContent = content;
        this._debouncedSave(content);
    }

    static styles = [
        shared,
        css`
            :host {
                display: contents;
                position: relative;
            }

            ptc-dashboard-tile {
                --tile-content-padding: 0;
            }

            ptc-rich-text-editor {
                border: solid 1px transparent;
                border-top-left-radius: 0;
                border-top-right-radius: 0;
                border-bottom-left-radius: var(--border-radius);
                border-bottom-right-radius: var(--border-radius);
                height: 100%;
                overflow: hidden;
                isolation: isolate;
                z-index: 4;
            }

            ptc-rich-text-editor:focus {
                border-color: var(--color-primary);
            }

            .save-indicator {
                display: flex;
                align-items: center;
                font-size: 1rem;
                gap: 0.25rem;
                color: var(--color-primary);
            }

            [part="spinner"] {
                font-size: 0.75rem;
            }

            [part="spinner"],
            [part="icon"] {
                width: 1lh;
                height: 1lh;
                line-height: inherit;

                transition: opacity 0.2s ease-in-out;

                @starting-style {
                    opacity: 0;
                }
            }
        `,
    ];

    render() {
        return html`
            <ptc-dashboard-tile
                style="--tile-icon-color: var(--yellow)"
                label=${DASHBOARD_CONFIG.tiles.notes.label}
                icon="icon.action.edit"
                .size=${this.size}
                tileType="notes"
                @tile-size-change=${(e: CustomEvent) => {
                    this.size = e.detail.size;
                }}
            >
                ${this._isSaving
                    ? html`<span slot="header" class="save-indicator">
                          <ptc-ui-icon name="icon.action.save" part="icon"></ptc-ui-icon>
                          <ptc-ui-spinner part="spinner" active></ptc-ui-spinner>
                      </span>`
                    : ""}
                ${this._notesTask.render({
                    initial: () => html``,
                    pending: () => html`
                        <ptc-ui-spinner
                            class="double-padded place-self-center"
                            active
                            style="font-size: 1rem"
                        ></ptc-ui-spinner>
                    `,
                    error: () =>
                        html`<div class="double-padded text-centering">Notizen konnten nicht geladen werden!</div>`,

                    complete: (note) => {
                        if (note === "admin_without_profile") {
                            return html`<div class="double-padded subtle text-centering">
                                Als Administrator ohne Mitarbeiterprofil in diesem Unternehmen kannst du hier keine
                                Notizen erstellen oder bearbeiten.
                            </div>`;
                        }
                        return html`<ptc-rich-text-editor
                            .content=${note?.content || "<p>Platz für deine Gedanken 💭</p>"}
                            @content-changed=${this._handleContentChange}
                        ></ptc-rich-text-editor>`;
                    },
                })}
            </ptc-dashboard-tile>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "ptc-dashboard-tile-notes": DashboardTileNotes;
    }
}
